import { useState } from 'react';
import { H5, Tab, Tabs, Tag, Text } from '@blueprintjs/core';
import { Focusable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { format, formatDistanceToNow } from 'date-fns';

import { Table } from '../../shared';
import { Box, Flex } from '../../shared/components';
import { type FrequentContact, type FrequentContactsSection, getMemberDisplayName, type Member } from '../model';
import { RECENTLY_ACTIVE_LIMIT, RECENTLY_ACTIVE_WINDOW_DAYS } from '../namedContacts/constants';

const PLACEHOLDER = 'No members to show yet.';
const FREQUENT_CONTACTS_PLACEHOLDER = 'No frequent support contacts to show yet.';
const ZENDESK_TICKET_URL = 'https://launchdarklysupport.zendesk.com/agent/tickets';

type PowerUsersPanelProps = {
  recentlyActive?: Member[];
  isLoadingRecentlyActive?: boolean;
  recentlyActiveError?: Error | null;
  frequentContacts?: FrequentContactsSection | null;
  isLoadingFrequentContacts?: boolean;
  frequentContactsError?: Error | null;
};

function Muted({ children }: { children: string }) {
  return <span className="bp4-text-muted">{children}</span>;
}

function formatDay(value: string | null) {
  if (!value) {
    return <Muted>Never</Muted>;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? <Muted>Never</Muted> : format(date, 'MM/dd/yyyy');
}

function lastTicket(contact: FrequentContact) {
  const day = formatDay(contact.lastTicketAt);
  if (!contact.lastTicketId) {
    return day;
  }
  return (
    <a href={`${ZENDESK_TICKET_URL}/${contact.lastTicketId}`} target="_blank" rel="noopener noreferrer">
      {day}
    </a>
  );
}

function RecentlyActivePanel({
  recentlyActive,
  isLoading,
  error,
}: {
  recentlyActive: Member[];
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading) {
    return <Text>Loading recently active members...</Text>;
  }
  if (error) {
    return <Text className="error-message">Failed to load recently active members</Text>;
  }
  if (recentlyActive.length === 0) {
    return <Text className="bp4-text-muted">{PLACEHOLDER}</Text>;
  }
  return (
    <>
      <Text className="bp4-text-muted">
        Members who have been active in the last {RECENTLY_ACTIVE_WINDOW_DAYS} days, capped at the top{' '}
        {RECENTLY_ACTIVE_LIMIT}
      </Text>
      <Table>
        <thead>
          <tr>
            <td>Name</td>
            <td>Email</td>
            <td>Role</td>
            <td>Last seen</td>
          </tr>
        </thead>
        <tbody>
          {recentlyActive.map((member) => (
            <tr key={member._id}>
              <td>{getMemberDisplayName(member)}</td>
              <td>{member.email}</td>
              <td>
                <Tag>{member.role}</Tag>
              </td>
              <td>
                {member._lastSeen ? (
                  <TooltipTrigger>
                    <Focusable>
                      <span role="button" tabIndex={0} style={{ cursor: 'default' }}>
                        {formatDistanceToNow(member._lastSeen, { addSuffix: true })}
                      </span>
                    </Focusable>
                    <Tooltip placement="top">{format(member._lastSeen, 'MM/dd/yyyy h:mm a')}</Tooltip>
                  </TooltipTrigger>
                ) : (
                  <Muted>Never</Muted>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

function FrequentContactsPanel({
  section,
  isLoading,
  error,
}: {
  section: FrequentContactsSection | null | undefined;
  isLoading: boolean;
  error: Error | null;
}) {
  if (isLoading) {
    return <Text>Loading frequent support contacts...</Text>;
  }
  if (error) {
    return <Text className="error-message">Failed to load frequent support contacts</Text>;
  }
  const contacts = section?.contacts ?? [];
  if (!section || contacts.length === 0) {
    return <Text className="bp4-text-muted">{FREQUENT_CONTACTS_PLACEHOLDER}</Text>;
  }
  return (
    <>
      <Text className="bp4-text-muted">
        Members who have raised tickets with Support in the last {section.meta.windowDays} days
      </Text>
      <Table>
        <thead>
          <tr>
            <td>Name</td>
            <td>Email</td>
            <td>Tickets</td>
            <td>Last ticket</td>
          </tr>
        </thead>
        <tbody>
          {contacts.map((contact) => (
            <tr key={contact.zendeskUserId ?? contact.requesterEmail ?? contact.requesterName ?? ''}>
              <td>{contact.requesterName ?? contact.requesterEmail}</td>
              <td>{contact.requesterEmail}</td>
              <td>{contact.tickets}</td>
              <td>{lastTicket(contact)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

export function PowerUsersPanel({
  recentlyActive = [],
  isLoadingRecentlyActive = false,
  recentlyActiveError = null,
  frequentContacts,
  isLoadingFrequentContacts = false,
  frequentContactsError = null,
}: PowerUsersPanelProps) {
  const [selectedTab, setSelectedTab] = useState<string>('recentlyActive');

  return (
    <Flex>
      <Box width={1} p={2}>
        <div data-test-id="power-users-panel">
          <H5 style={{ marginTop: 0, marginBottom: '8px' }}>Power Users</H5>
          <Tabs
            id="powerUsersTabs"
            selectedTabId={selectedTab}
            onChange={(newTabId) => setSelectedTab(newTabId as string)}
            animate={false}
            renderActiveTabPanelOnly
          >
            <Tab
              id="recentlyActive"
              title="Recently active"
              panel={
                <RecentlyActivePanel
                  recentlyActive={recentlyActive}
                  isLoading={isLoadingRecentlyActive}
                  error={recentlyActiveError}
                />
              }
            />
            <Tab
              id="frequentContacts"
              title="Frequent contacts"
              panel={
                <FrequentContactsPanel
                  section={frequentContacts}
                  isLoading={isLoadingFrequentContacts}
                  error={frequentContactsError}
                />
              }
            />
          </Tabs>
        </div>
      </Box>
    </Flex>
  );
}
